import { Stack, Avatar } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import Dot from '../Utils/Dot';
import { Typography } from '@ts/storybook';

const getCurrentDate = () => {
  const currentDate = dayjs().format('DD/MM/YYYY HH:MM');
  return currentDate;
};

interface TaskTableProps {
  isInRisk?: boolean;
  isTimeDedicated?: boolean;
}

const TasksTable = ({ isInRisk, isTimeDedicated }: TaskTableProps) => {
  let columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'name',
      headerName: 'Asignee',
      width: 180,
      renderCell: params => {
        const { row } = params;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar alt={row.name} src={row.profileImage} />
            <span style={{ marginLeft: 10 }}>{row.name}</span>
          </div>
        );
      },
    },
    { field: 'type', headerName: 'Task type', width: 130 },
    {
      field: 'progress',
      headerName: 'Progress',
      width: 200,
      renderCell: params => {
        const value = Number(params.value);
        const progress = value >= 0 ? Math.min(100, value) : 0;
        const color = value >= 0 ? '#00c853' : '#f44336';

        return (
          <div
            style={{
              width: '100%',
              height: 25,
              backgroundColor: '#f5f5f5',
              border: '1px solid #ccc',
              borderRadius: 2,
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: color,
                borderRadius: 2,
              }}
            />
            <div
              style={{
                position: 'relative',
                top: '-22px',
                left: '72px',
                background: '#0000008c',
                width: 'fit-content',
                color: 'white',
                paddingInline: '5px',
                borderRadius: '6px',
              }}
            >{`${progress}%`}</div>
          </div>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      renderCell: params => {
        let color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
        let title;

        switch (Number(params.value)) {
          case 0:
            color = 'warning';
            title = 'Checking';
            break;
          case 1:
            color = 'success';
            title = 'Executed';
            break;
          case 2:
            color = 'error';
            title = 'Assigned';
            break;
          default:
            color = 'primary';
            title = 'None';
        }

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Dot color={color} />
            <Typography text={title} />
          </Stack>
        );
      },
    },
    { field: 'time', headerName: 'Due Date', width: 150, valueGetter: getCurrentDate },
  ];

  let rows: any = [
    {
      id: 1,
      manager: '',
      name: 'Matt Kings',
      profileImage: '',
      date: new Date().getDate(),
      type: 'Truck load',
      progress: 75,
      status: 0,
      warning: 0,
    },
    {
      id: 2,
      manager: '',
      name: 'Joe Green',
      profileImage: '',
      date: new Date(),
      type: 'Truck load',
      progress: 50,
      status: 1,
      warning: 1,
    },
    {
      id: 3,
      manager: '',
      name: 'Alfred Bruhn',
      profileImage: '',
      date: new Date(),
      type: 'RTI maintenance',
      progress: 90,
      status: 2,
      warning: 2,
    },
    {
      id: 4,
      manager: '',
      name: 'Judie Falls',
      profileImage: '',
      date: new Date(),
      type: 'Training',
      progress: -10,
      status: undefined,
      warning: undefined,
    },
  ];

  if (isInRisk) {
    columns = [
      { field: 'id', headerName: 'ID', width: 70 },
      {
        field: 'name',
        headerName: 'Asignee',
        width: 180,
        renderCell: params => {
          const { row } = params;
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Avatar alt={row.name} src={row.profileImage} />
              <span style={{ marginLeft: 10 }}>{row.name}</span>
            </div>
          );
        },
      },
      { field: 'type', headerName: 'Location', width: 130 },
      {
        field: 'progress',
        headerName: 'Severity',
        width: 200,
        renderCell: params => {
          const value = Number(params.value);
          const progress = value >= 0 ? Math.min(100, value) : 0;
          const color = value >= 0 ? '#ff0053' : '#ff0030';

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: 25,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ccc',
                  borderRadius: 2,
                }}
              >
                <div
                  style={{
                    width: `${progress * 10}%`,
                    height: 25,
                    backgroundColor: color,
                    borderRadius: 2,
                  }}
                />
                <div
                  style={{
                    position: 'relative',
                    top: '-25px',
                    left: '76px',
                    background: '#0000008c',
                    width: 'fit-content',
                    color: 'white',
                    height: 'inherit',
                    paddingInline: '5px',
                    borderRadius: '6px',
                  }}
                >
                  <span style={{ position: 'relative', top: '-11px' }}>{`${progress}`}</span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        field: 'warning',
        headerName: 'Warning',
        width: 200,
        renderCell: params => {
          let color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | undefined;
          let title;

          switch (Number(params.value)) {
            case 0:
              color = 'warning';
              title = 'SOS button';
              break;
            case 1:
              color = 'success';
              title = 'Impact';
              break;
            case 2:
              color = 'error';
              title = '5% battery';
              break;
            default:
              color = undefined;
              title = 'no movement';
          }

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Dot color={color} />
                <Typography text={title} />
              </Stack>
            </div>
          );
        },
      },
      { field: 'time', headerName: 'Event time', width: 200, valueGetter: getCurrentDate },
      {
        field: 'status',
        headerName: 'Status',
        width: 200,
        renderCell: params => {
          let color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | undefined;
          let title;

          switch (Number(params.value)) {
            case 0:
              color = 'warning';
              title = 'Ack';
              break;
            case 1:
              color = 'success';
              title = 'Solved';
              break;
            case 2:
              color = 'error';
              title = 'Open';
              break;
            default:
              color = undefined;
              title = 'Closed';
          }

          return (
            <div className="flex items-center h-full">
              <Stack direction="row" spacing={1} alignItems="center">
                <Dot color={color} />
                <Typography text={title} />
              </Stack>
            </div>
          );
        },
      },
      {
        field: 'manager',
        headerName: 'HSE Manager',
        width: 180,
        renderCell: params => {
          const { row } = params;
          return (
            <div className="flex items-center">
              <span className="ml-2.5">{row.manager}</span>
            </div>
          );
        },
      },
    ];
    rows = [
      {
        id: 1,
        manager: 'Saif Rehman',
        name: 'Matt Kings',
        profileImage: '',
        date: new Date().getDate(),
        type: 'Build 3 - Floor 4',
        progress: 9,
        status: 0,
        warning: 0,
      },
      {
        id: 2,
        manager: 'Saif Rehman',
        name: 'Joe Green',
        profileImage: '',
        date: new Date(),
        type: 'Parking area',
        progress: 5,
        status: 1,
        warning: 1,
      },
      {
        id: 3,
        manager: '',
        name: 'Alfred Bruhn',
        profileImage: '',
        date: new Date(),
        type: 'Warehouse',
        progress: 9,
        status: 2,
        warning: 2,
      },
      {
        id: 4,
        manager: 'Thomas Lee',
        name: 'Joe Doll',
        profileImage: '',
        date: new Date(),
        type: 'Build 2 - Floor 1',
        progress: 3,
        status: undefined,
        warning: 2,
      },
      {
        id: 5,
        manager: 'Thomas Lee',
        name: 'Alfred Bruhn',
        profileImage: '',
        date: new Date(),
        type: 'Build 3 - Floor 4',
        progress: 0,
        status: undefined,
        warning: 2,
      },
      {
        id: 6,
        manager: 'Thomas Lee',
        name: 'Bob Mohamed',
        profileImage: '',
        date: new Date(),
        type: 'Parking area',
        progress: 5,
        status: 1,
        warning: 1,
      },
      {
        id: 7,
        manager: '',
        name: 'Anas Al Maani',
        profileImage: '',
        date: new Date(),
        type: 'Wharehouse',
        progress: 7,
        status: 2,
        warning: 1,
      },
      {
        id: 8,
        manager: 'Saif Rehman',
        name: 'Suleiman Younis',
        profileImage: '',
        date: new Date(),
        type: 'Build 2 - Floor 1',
        progress: 6,
        status: 0,
        warning: 0,
      },
    ];
  }

  if (isTimeDedicated) {
    columns = [
      { field: 'id', headerName: 'ID', width: 70 },
      {
        field: 'name',
        headerName: 'Worker',
        width: 180,
        renderCell: params => {
          const { row } = params;
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Avatar alt={row.name} src={row.profileImage} />
              <span style={{ marginLeft: 10 }}>{row.name}</span>
            </div>
          );
        },
      },
      { field: 'type', headerName: 'Team', width: 130 },
      {
        field: 'progress',
        headerName: 'Progress',
        width: 240,
        renderCell: params => {
          const value = Number(params.value);
          const progress = value >= 0 ? Math.min(100, value) : 0;
          const color = value >= 0 ? '#6e51cf' : '#00ff00';

          return (
            <div
              style={{
                width: '100%',
                height: 25,
                backgroundColor: '#f5f5f5',
                border: '1px solid #ccc',
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: color,
                  borderRadius: 2,
                }}
              />
              <div
                style={{
                  position: 'relative',
                  top: '-22px',
                  left: '72px',
                  background: '#0000008c',
                  width: 'fit-content',
                  color: 'white',
                  paddingInline: '5px',
                  borderRadius: '6px',
                }}
              >{`${progress}%`}</div>
            </div>
          );
        },
      },
      { field: 'time', headerName: 'Time/Week', width: 150 },
      { field: 'time1', headerName: 'Time/Month', width: 150 },
    ];

    rows = [
      {
        id: 1,
        manager: '',
        name: 'Matt Kings',
        profileImage: '',
        date: new Date().getDate(),
        type: 'Contractor A',
        progress: 75,
        status: 0,
        warning: 0,
        time: '43h',
        time1: '222h',
      },
      {
        id: 2,
        manager: '',
        name: 'Joe Green',
        profileImage: '',
        date: new Date(),
        type: 'Contractor A',
        progress: 50,
        status: 1,
        warning: 1,
        time: '39h',
        time1: '237h',
      },
      {
        id: 3,
        manager: '',
        name: 'Alfred Bruhn',
        profileImage: '',
        date: new Date(),
        type: 'Contractor B',
        progress: 90,
        status: 2,
        warning: 2,
        time: '34h',
        time1: '237h',
      },
      {
        id: 4,
        manager: '',
        name: 'Judie Falls',
        profileImage: '',
        date: new Date(),
        type: 'Contractor B',
        progress: -10,
        status: undefined,
        warning: undefined,
        time: '42h',
        time1: '294h',
      },
    ];
  }

  return <DataGrid autoHeight={true} rows={rows} columns={columns} checkboxSelection={false} style={{ borderWidth: '0px' }} />;
};

export default TasksTable;
